import type { PersistedTimer, UserSettings } from './types';

const SETTINGS_KEY = 'teams-timer.settings.v1';
const TIMER_KEY = 'teams-timer.active.v1';

export const defaultSettings: UserSettings = {
  minutes: 5,
  seconds: 0,
  soundEnabled: true,
  animationsEnabled: true,
  thresholds: { warning: 0.3, danger: 0.1 },
  theme: 'dark',
  pipWidth: 520,
  pipHeight: 260,
};

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export class StorageService {
  constructor(private readonly store: Storage = localStorage) {}

  loadSettings(): UserSettings {
    const saved = safeParse<Partial<UserSettings>>(this.store.getItem(SETTINGS_KEY));
    if (!saved) return structuredClone(defaultSettings);

    return {
      ...defaultSettings,
      ...saved,
      minutes: validInteger(saved.minutes, 0, 999, defaultSettings.minutes),
      seconds: validInteger(saved.seconds, 0, 59, defaultSettings.seconds),
      pipWidth: validInteger(saved.pipWidth, 320, 1200, defaultSettings.pipWidth),
      pipHeight: validInteger(saved.pipHeight, 180, 900, defaultSettings.pipHeight),
      thresholds: {
        warning: validRatio(saved.thresholds?.warning, defaultSettings.thresholds.warning),
        danger: validRatio(saved.thresholds?.danger, defaultSettings.thresholds.danger),
      },
      theme: 'dark',
    };
  }

  saveSettings(settings: UserSettings): void {
    this.store.setItem(SETTINGS_KEY, JSON.stringify(settings));
  }

  loadTimer(): PersistedTimer | null {
    const saved = safeParse<PersistedTimer>(this.store.getItem(TIMER_KEY));
    if (!saved || !['idle', 'running', 'paused'].includes(saved.phase)) return null;
    return saved;
  }

  saveTimer(timer: PersistedTimer): void {
    if (timer.phase === 'idle') {
      this.clearTimer();
      return;
    }
    this.store.setItem(TIMER_KEY, JSON.stringify(timer));
  }

  clearTimer(): void {
    this.store.removeItem(TIMER_KEY);
  }
}

function validInteger(value: unknown, min: number, max: number, fallback: number): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max
    ? value
    : fallback;
}

function validRatio(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1
    ? value
    : fallback;
}
